<template>
  <div style="padding-top: 16px" id="page-container">
    <div class="page-section jobs_container">
      <div :class="containerClass">
        <!-- Header -->
        <div class="d-flex flex-column flex-sm-row align-items-sm-center mb-16pt" style="white-space: nowrap">
          <h2 class="mb-0">
            Messages
            <i
              class="material-icons icon-16pt clickable-item"
              v-b-popover.hover.top="`Send bulk emails and SMS messages to students of your programs.`"
              >info</i
            >
          </h2>
          <div class="w-auto ml-sm-auto mb-sm-0">
            <b-btn @click.prevent="openComposeModal()" variant="primary">
              <i class="material-icons icon-16pt mr-1">add</i>Compose Message
            </b-btn>
          </div>
        </div>

        <!-- Tabs -->
        <b-tabs v-model="activeTab" class="mb-0" @input="onTabChange">
          <b-tab>
            <template #title>
              <i class="material-icons icon-16pt mr-1" style="vertical-align: middle;">send</i> Sent
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <i class="material-icons icon-16pt mr-1" style="vertical-align: middle;">inbox</i>
              Inbox
              <b-badge v-if="inboxUnreadCount > 0" variant="danger" pill class="ml-1" style="font-size: 0.65rem;">
                {{ inboxUnreadCount }}
              </b-badge>
            </template>
          </b-tab>
        </b-tabs>

        <!-- ── SENT TAB ── -->
        <div v-if="activeTab === 0">
          <!-- Filters Row -->
          <div class="d-flex flex-wrap mt-16pt mb-16pt" style="gap: 8px;">
            <div style="min-width: 200px; flex: 1;">
              <b-form-input v-model="searchTerm" placeholder="Search by subject..." @input="onSearch" />
            </div>
            <div style="min-width: 160px;">
              <b-form-select v-model="filters.send_type" :options="sendTypeOptions" @change="onFilterApplied" />
            </div>
            <div style="min-width: 160px;">
              <b-form-select v-model="filters.status" :options="statusOptions" @change="onFilterApplied" />
            </div>
            <div style="min-width: 180px;">
              <v-select
                class="form-control v-select-custom"
                label="title"
                v-model="filters.program"
                :reduce="p => p.id"
                placeholder="All Programs"
                :options="allPrograms"
                :loading="areProgramsLoading"
                @input="onFilterApplied"
                :clearable="true"
              />
            </div>
          </div>

          <page-separator
            :title="
              !isLoading
                ? `showing ${totalMessages ? `${pageFromCount}-${pageToCount}` : 0} of ${totalMessages} messages`
                : ''
            "
          />

          <!-- Empty State -->
          <div class="d-flex justify-content-center" v-if="!totalMessages && !isLoading">
            <div class="card card-block card-stretch card-height col-md-12">
              <div class="card-body text-center">
                <i class="fas fa-paper-plane fa-lg text-muted mt-4 mb-4" style="font-size: 3rem" />
                <h4>No Messages Sent Yet</h4>
                <p class="text-muted">
                  You haven't sent any bulk messages yet. Click "Compose Message" to get started.
                </p>
                <b-btn @click.prevent="openComposeModal()" variant="primary" class="mt-2">
                  <i class="material-icons icon-16pt mr-1">send</i>Compose Message
                </b-btn>
              </div>
            </div>
          </div>

          <!-- Skeleton Loader -->
          <b-skeleton-wrapper :loading="isLoading" v-else>
            <template #loading>
              <div class="row card-group-row mb-lg-12pt home-card">
                <div class="col-sm-12 card-group-row__col" v-for="item in [1, 2, 3]" :key="item">
                  <div class="card overlay--show card-lg overlay--primary-dodger-blue card-group-row__card">
                    <div class="card-header d-flex align-items-center">
                      <a href="#" @click.prevent class="card-title flex"><b-skeleton width="60%"></b-skeleton></a>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <b-skeleton width="100%"></b-skeleton>
                      <b-skeleton width="70%"></b-skeleton>
                      <b-skeleton width="40%"></b-skeleton>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Message Cards -->
            <div class="row card-group-row mb-lg-12pt home-card">
              <div class="col-sm-12 card-group-row__col" v-for="msg in messages" :key="msg.id">
                <div
                  class="card overlay--show card-lg overlay--primary-dodger-blue card-group-row__card clickable-item"
                  @click="openDeliveryLogDrawer(msg)"
                >
                  <div
                    class="card-header d-flex align-items-center justify-content-between flex-wrap"
                    style="gap: 8px;"
                  >
                    <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                      <span class="card-title mb-0" style="margin-right: 8px;">{{ msg.subject }}</span>
                      <span :class="sentStatusBadgeClass(msg)" style="text-transform: none; font-size: 0.75rem;">
                        {{ sentStatusLabel(msg) }}
                      </span>
                      <span :class="statusBadgeClass(msg.status)" style="text-transform: none; font-size: 0.75rem;">
                        {{ statusLabel(msg.status) }}
                      </span>
                    </div>
                    <div>
                      <b-btn
                        v-if="msg.send_type === 'email' || msg.send_type === 'both'"
                        size="sm"
                        variant="outline-primary"
                        style="font-size: 0.72rem; padding: 2px 8px;"
                        @click.stop="openInboxForCampaign(msg.id)"
                      >
                        <i class="material-icons icon-16pt" style="vertical-align: middle;">inbox</i> View Replies
                      </b-btn>
                    </div>
                  </div>

                  <div class="card-body d-flex flex-column">
                    <div class="mb-2 text-muted" style="font-size: 0.9rem;">
                      {{ truncate(trim(stripHtml(msg.message)), { length: 200, separator: /,? +/ }) }}
                    </div>

                    <div class="card-button">
                      <ul>
                        <li>
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">{{ sendTypeIcon(msg.send_type) }}</span>
                            {{ sendTypeLabel(msg.send_type) }}
                          </a>
                        </li>
                        <li v-if="get(msg, 'program.title')">
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">school</span>
                            {{ get(msg, 'program.title') }}
                          </a>
                        </li>
                        <li v-else>
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">people</span>
                            All Students
                          </a>
                        </li>
                        <li>
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">schedule</span>
                            {{ formatDateTime(msg.created_at) }}
                          </a>
                        </li>
                        <li v-if="msg.status === 'sent' || msg.total_recipients > 0">
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">group</span>
                            {{ deliveryCountLabel(msg) }}
                          </a>
                        </li>
                        <li v-if="get(msg, 'sent_by_name')">
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">person</span>
                            {{ msg.sent_by_name }}
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-skeleton-wrapper>

          <pagination
            v-if="totalMessages > messages.length"
            v-model="currentPage"
            :total-rows="totalMessages"
            :per-page="perPage"
            @change="onPageChange"
            aria-controls="bulk-messages-table"
          />
        </div>

        <!-- ── INBOX TAB ── -->
        <div v-if="activeTab === 1">
          <div class="d-flex flex-wrap mt-16pt mb-16pt align-items-center" style="gap: 8px;">
            <div style="min-width: 200px; flex: 1;">
              <b-form-input v-model="inboxSearch" placeholder="Search by sender or subject..." @input="onInboxSearch" />
            </div>
          </div>

          <!-- Campaign Filter Badge -->
          <div v-if="inboxCampaignFilter" class="mb-2">
            <b-badge
              variant="info"
              class="d-inline-flex align-items-center"
              style="font-size: 0.85rem; padding: 6px 10px;"
            >
              <i class="material-icons mr-1" style="font-size: 16px;">filter_list</i>
              Campaign Filter Active
              <b-btn
                variant="link"
                size="sm"
                class="p-0 ml-2 text-white"
                @click="clearCampaignFilter"
                style="font-size: 16px; line-height: 1;"
              >
                <i class="material-icons" style="font-size: 16px;">close</i>
              </b-btn>
            </b-badge>
          </div>

          <page-separator
            :title="
              !isInboxLoading
                ? `showing ${totalReplies ? `${inboxPageFrom}-${inboxPageTo}` : 0} of ${totalReplies} replies`
                : ''
            "
          />

          <!-- Inbox empty -->
          <div class="d-flex justify-content-center" v-if="!totalReplies && !isInboxLoading">
            <div class="card card-block card-stretch card-height col-md-12">
              <div class="card-body text-center">
                <i class="material-icons text-muted mt-4 mb-4" style="font-size: 3rem;">inbox</i>
                <h4>Inbox is Empty</h4>
                <p class="text-muted">Student replies to your email campaigns will appear here.</p>
              </div>
            </div>
          </div>

          <b-skeleton-wrapper :loading="isInboxLoading" v-else>
            <template #loading>
              <div v-for="i in [1, 2, 3, 4]" :key="i" class="card mb-2">
                <div class="card-body py-3 d-flex align-items-center" style="gap: 12px;">
                  <b-skeleton type="avatar" />
                  <div class="flex">
                    <b-skeleton width="40%" />
                    <b-skeleton width="70%" />
                  </div>
                  <b-skeleton width="80px" />
                </div>
              </div>
            </template>

            <div
              v-for="thread in replies"
              :key="thread.sender_email + '-' + thread.bulk_message"
              class="card mb-2 clickable-item"
              :class="{ 'border-left-primary': !thread.is_read }"
              style="border-left: 3px solid transparent;"
              :style="!thread.is_read ? 'border-left-color: #0069d9;' : ''"
              @click="openThread(thread)"
            >
              <div class="card-body py-3 d-flex align-items-center flex-wrap" style="gap: 12px;">
                <div
                  class="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                  style="width: 40px; height: 40px; background: #e9ecef; font-weight: 600; font-size: 1rem;"
                >
                  {{ replyInitial(thread) }}
                </div>
                <div class="flex" style="min-width: 0;">
                  <div class="d-flex align-items-center" style="gap: 6px;">
                    <span :class="!thread.is_read ? 'font-weight-bold' : ''">
                      {{ thread.sender_user_name || thread.sender_email }}
                    </span>
                    <b-badge v-if="!thread.is_read" variant="primary" style="font-size: 0.6rem;">UNREAD</b-badge>
                    <b-badge v-if="!thread.bulk_message" variant="secondary" style="font-size: 0.6rem;">Direct</b-badge>
                  </div>
                  <div class="text-muted text-truncate" style="font-size: 0.85rem; max-width: 500px;">
                    {{ thread.subject }}
                  </div>
                </div>
                <div class="text-muted flex-shrink-0" style="font-size: 0.8rem; white-space: nowrap;">
                  {{ formatDateTime(thread.latest_reply_at) }}
                </div>
              </div>
            </div>
          </b-skeleton-wrapper>

          <pagination
            v-if="totalReplies > replies.length"
            v-model="inboxCurrentPage"
            :total-rows="totalReplies"
            :per-page="perPage"
            @change="onInboxPageChange"
          />
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════
         COMPOSE EMAIL MODAL — Gmail-like
    ══════════════════════════════════════ -->
    <b-modal
      id="compose-bulk-message-modal"
      size="xl"
      centered
      hide-footer
      hide-header
      @hidden="resetForm"
      body-class="p-0"
    >
      <b-form @submit.prevent="onSubmit" class="p-0">
        <!-- Custom Header -->
        <div
          class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom"
          style="background: #f8f9fa;"
        >
          <h5 class="mb-0" style="font-weight: 500; font-size: 1rem; color: #202124;">Compose Email</h5>
          <b-btn
            variant="link"
            class="p-0 text-muted"
            @click="$bvModal.hide('compose-bulk-message-modal')"
            style="font-size: 1.5rem; line-height: 1;"
          >
            <i class="material-icons">close</i>
          </b-btn>
        </div>

        <div class="px-4 py-3">
          <!-- To Field with Student Capsules -->
          <div class="mb-3">
            <div class="d-flex align-items-start pb-2 border-bottom" style="gap: 12px;">
              <label class="text-muted mb-0 pt-1" style="min-width: 50px; font-size: 0.85rem; font-weight: 500;"
                >To</label
              >
              <div class="flex-grow-1">
                <!-- Audience Selection -->
                <div class="d-flex align-items-center flex-wrap mb-2" style="gap: 12px;">
                  <b-form-radio v-model="audienceType" value="all" name="audience_type" @change="onAudienceTypeChange">
                    <span style="font-size: 0.85rem;">All Students</span>
                  </b-form-radio>
                  <b-form-radio
                    v-model="audienceType"
                    value="program"
                    name="audience_type"
                    @change="onAudienceTypeChange"
                  >
                    <span style="font-size: 0.85rem;">Program Specific</span>
                  </b-form-radio>
                  <b-form-radio
                    v-model="audienceType"
                    value="manual"
                    name="audience_type"
                    @change="onAudienceTypeChange"
                  >
                    <span style="font-size: 0.85rem;">Manual Emails</span>
                  </b-form-radio>
                </div>

                <div v-if="audienceType === 'program'" class="mb-2" style="max-width: 360px">
                  <v-select
                    class="form-control v-select-custom"
                    label="title"
                    v-model="form.program"
                    :reduce="p => p.id"
                    placeholder="Select Program..."
                    :options="allPrograms"
                    :loading="areProgramsLoading"
                    :clearable="true"
                    @input="onProgramChange"
                    style="font-size: 0.85rem;"
                  />
                </div>

                <!-- Manual Email Input -->
                <div v-if="audienceType === 'manual'" class="mt-1">
                  <b-form-input
                    v-model.trim="manualEmailInput"
                    placeholder="Type student name or email"
                    @input="onManualEmailInput"
                    @keydown.enter.prevent="addManualRecipientsFromInput"
                  />
                  <small class="text-muted d-block mt-1" style="font-size: 0.78rem;">
                    Search students by name/email and click to add, or press Enter for direct email.
                  </small>

                  <div
                    v-if="manualStudentCandidates.length > 0"
                    class="d-flex flex-wrap align-items-center mt-2"
                    style="gap: 6px;"
                  >
                    <span
                      v-for="student in manualVisibleStudents"
                      :key="`manual-email-${recipientKey(student)}`"
                      class="d-inline-flex align-items-center manual-recipient-chip"
                      :class="{ 'manual-recipient-chip--selected': isManualRecipientSelected(student) }"
                      @click="toggleManualRecipientFromStudent(student)"
                    >
                      <span
                        class="rounded-circle d-inline-flex align-items-center justify-content-center mr-1"
                        style="width: 18px; height: 18px; background: #e9ecef; font-size: 0.62rem; font-weight: 600;"
                      >
                        {{ recipientInitial(student) }}
                      </span>
                      <span class="d-inline-flex flex-column align-items-start" style="line-height: 1.1;">
                        <span style="font-weight: 500;">{{ recipientName(student) }}</span>
                        <span class="text-muted">{{ recipientEmail(student) }}</span>
                      </span>
                      <i v-if="isManualRecipientSelected(student)" class="material-icons ml-1 manual-recipient-check">done</i>
                    </span>
                    <b-btn
                      v-if="manualStudentCandidates.length > 5"
                      variant="link"
                      size="sm"
                      class="p-0 text-primary"
                      @click="toggleShowAllStudents"
                      style="font-size: 0.7rem; text-decoration: none; font-weight: 500;"
                    >
                      {{ showAllStudents ? 'See less' : `+${manualStudentCandidates.length - 5} more` }}
                    </b-btn>
                  </div>

                  <div v-else-if="isDashboardStudentsLoading" class="mt-2">
                    <b-spinner small class="mr-2" />
                    <span class="text-muted" style="font-size: 0.82rem;">Loading students...</span>
                  </div>

                  <div v-else class="text-muted mt-2" style="font-size: 0.82rem;">
                    No students found.
                  </div>

                  <div
                    v-if="manualRecipients.length > 0"
                    class="d-flex flex-wrap align-items-center mt-2"
                    style="gap: 6px;"
                  >
                    <span
                      v-for="email in manualRecipients"
                      :key="email"
                      class="badge badge-light d-inline-flex align-items-center"
                      style="font-size: 0.72rem; padding: 5px 9px; font-weight: 400; color: #495057; border: 1px solid #dee2e6;"
                    >
                      <span
                        class="rounded-circle d-inline-flex align-items-center justify-content-center mr-1"
                        style="width: 18px; height: 18px; background: #e9ecef; font-size: 0.62rem; font-weight: 600;"
                      >
                        {{ manualRecipientInitial(email) }}
                      </span>
                      {{ email }}
                      <button
                        type="button"
                        class="btn btn-link p-0 ml-1 text-muted"
                        style="line-height: 1;"
                        @click="removeManualRecipient(email)"
                      >
                        <i class="material-icons" style="font-size: 14px;">close</i>
                      </button>
                    </span>
                  </div>
                </div>

                <div
                  v-else-if="audienceType === 'program' && !form.program"
                  class="text-muted"
                  style="font-size: 0.82rem;"
                >
                  Select a program to view recipients.
                </div>

                <!-- Student Email Capsules -->
                <div v-else-if="dashboardStudents.length > 0">
                  <div class="d-flex flex-wrap align-items-center" style="gap: 6px;">
                    <span
                      v-for="student in visibleStudents"
                      :key="recipientKey(student)"
                      class="badge badge-light d-inline-flex align-items-center"
                      style="font-size: 0.7rem; padding: 4px 8px; font-weight: 400; color: #495057; border: 1px solid #dee2e6;"
                    >
                      <span
                        class="rounded-circle d-inline-flex align-items-center justify-content-center mr-1"
                        style="width: 16px; height: 16px; background: #e9ecef; font-size: 0.6rem; font-weight: 500;"
                        >{{ recipientInitial(student) }}</span
                      >
                      {{ recipientEmail(student) }}
                    </span>
                    <b-btn
                      v-if="dashboardStudents.length > 5"
                      variant="link"
                      size="sm"
                      class="p-0 text-primary"
                      @click="toggleShowAllStudents"
                      style="font-size: 0.7rem; text-decoration: none; font-weight: 500;"
                    >
                      {{ showAllStudents ? 'See less' : `+${dashboardStudents.length - 5} more` }}
                    </b-btn>
                  </div>
                </div>
                <div v-else-if="audienceType !== 'manual' && isDashboardStudentsLoading">
                  <b-spinner small class="mr-2" />
                  <span class="text-muted" style="font-size: 0.9rem;">Loading students...</span>
                </div>
                <div v-else-if="audienceType !== 'manual'" class="text-muted" style="font-size: 0.82rem;">
                  No recipients found for the selected audience.
                </div>
              </div>
            </div>
          </div>

          <!-- Subject Field -->
          <div class="mb-3">
            <div class="d-flex align-items-center pb-2 border-bottom" style="gap: 12px;">
              <label
                for="subject"
                class="text-muted mb-0"
                style="min-width: 50px; font-size: 0.85rem; font-weight: 500;"
                >Subject</label
              >
              <b-input-group
                style="background: #f8f9fa; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;"
              >
                <b-form-input
                  id="subject"
                  v-model.trim="form.subject"
                  class="border-0 bg-transparent"
                  style="font-size: 0.9rem; padding-left: 0;"
                  :state="$v.form.subject.$dirty ? !$v.form.subject.$error : null"
                  placeholder=" Add a subject"
                />
              </b-input-group>
            </div>
          </div>

          <!-- Message Editor -->
          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <label class="text-muted mb-0" style="font-size: 0.85rem; font-weight: 500;">Message</label>
              <a
                href="#"
                class="text-primary"
                style="font-size: 0.75rem; text-decoration: none;"
                @click.prevent="openBookmarkDetails"
              >
                <i class="material-icons" style="font-size: 14px; vertical-align: middle;">bookmark</i> Bookmarks
              </a>
            </div>
            <editor
              :key="`editor-${editorKey}`"
              :api-key="tinemceApiKey"
              :init="{
                height: 320,
                menubar: false,
                branding: false,
                plugins: [
                  'advlist autolink lists link image charmap print preview anchor',
                  'searchreplace visualblocks code fullscreen',
                  'insertdatetime media table paste code help wordcount',
                ],
                toolbar:
                  'undo redo | formatselect | bookmarks | bold italic underline | ' +
                  'alignleft aligncenter alignright | bullist numlist | link removeformat',
                setup: function(editor) {
                  editor.ui.registry.addMenuButton('bookmarks', {
                    text: 'Bookmarks',
                    fetch: function(callback) {
                      var items = EMAIL_BOOKMARKS['bulk_messaging'].map(function(object) {
                        return Object.assign({}, object, {
                          onAction: function() {
                            editor.insertContent('&nbsp;' + object.value);
                          },
                        });
                      });
                      callback(items);
                    },
                  });
                },
              }"
              v-model="form.message"
            />
          </div>

          <!-- Validation Errors -->
          <div
            v-if="audienceType === 'manual' && manualEmailInput && !manualRecipients.length"
            class="alert alert-danger py-2 px-3 mb-2"
            style="font-size: 0.8rem;"
          >
            <i class="material-icons" style="font-size: 16px; vertical-align: middle;">error_outline</i>
            Please enter at least one valid email address.
          </div>
          <div
            v-if="$v.form.subject.$dirty && $v.form.subject.$error"
            class="alert alert-danger py-2 px-3 mb-2"
            style="font-size: 0.8rem;"
          >
            <i class="material-icons" style="font-size: 16px; vertical-align: middle;">error_outline</i>
            Subject is required.
          </div>
          <div
            v-if="$v.form.message.$dirty && $v.form.message.$error"
            class="alert alert-danger py-2 px-3 mb-2"
            style="font-size: 0.8rem;"
          >
            <i class="material-icons" style="font-size: 16px; vertical-align: middle;">error_outline</i>
            Message is required.
          </div>
        </div>

        <!-- Super Admin Organization Field -->
        <b-form-group v-if="isSuperAdmin" label="Organization ID" label-for="organization" class="mb-4">
          <b-form-input
            id="organization"
            v-model.number="form.organization"
            type="number"
            placeholder="Enter organization ID"
          />
        </b-form-group>

        <!-- Action Buttons -->
        <div
          class="d-flex justify-content-end align-items-center px-4 py-3 border-top"
          style="gap: 10px; background: #f8f9fa;"
        >
          <b-btn
            variant="light"
            size="sm"
            @click="$bvModal.hide('compose-bulk-message-modal')"
            style="font-size: 0.85rem;"
          >
            Cancel
          </b-btn>
          <b-btn variant="primary" size="sm" type="submit" :disabled="isSending" style="font-size: 0.85rem;">
            <b-spinner small v-if="isSending" class="mr-1" />
            {{ isSending ? 'Sending...' : 'Send Email' }}
          </b-btn>
        </div>
      </b-form>
    </b-modal>

    <!-- ══════════════════════════════════════
         DELIVERY LOG DRAWER
    ══════════════════════════════════════ -->
    <b-modal
      id="delivery-log-modal"
      :title="get(selectedMessage, 'subject', 'Delivery Log')"
      size="xl"
      centered
      ok-only
      ok-title="Close"
      ok-variant="light"
      @hidden="onDeliveryLogHidden"
    >
      <div v-if="selectedMessage">
        <!-- Summary Row -->
        <div class="row text-center mb-4">
          <div class="col-md-4">
            <div class="card h-100">
              <div class="card-body py-3">
                <span :class="statusBadgeClass(selectedMessage.status)" style="font-size: 0.8rem;">
                  {{ statusLabel(selectedMessage.status) }}
                </span>
                <small class="d-block text-muted mt-1">Status</small>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card h-100">
              <div class="card-body py-3">
                <h5 class="mb-0">{{ selectedMessage.total_recipients }}</h5>
                <small class="text-muted">Total</small>
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="selectedMessage.send_type === 'email' || selectedMessage.send_type === 'both'">
            <div class="card h-100">
              <div class="card-body py-3">
                <h5 class="mb-0 text-success">{{ selectedMessage.email_count }}</h5>
                <small class="text-muted">Emails Sent</small>
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="selectedMessage.send_type === 'sms' || selectedMessage.send_type === 'both'">
            <div class="card h-100">
              <div class="card-body py-3">
                <h5 class="mb-0 text-success">{{ selectedMessage.sms_count }}</h5>
                <small class="text-muted">SMS Sent</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Meta -->
        <ul class="list-group list-group-flush mb-3">
          <li class="list-group-item d-flex justify-content-between px-0">
            <span class="text-muted">Program</span>
            <span>{{ get(selectedMessage, 'program.title') || 'All Students' }}</span>
          </li>
          <li class="list-group-item d-flex justify-content-between px-0">
            <span class="text-muted">Sent by</span>
            <span>{{ selectedMessage.sent_by_name || '—' }}</span>
          </li>
          <li class="list-group-item d-flex justify-content-between px-0">
            <span class="text-muted">Date</span>
            <span>{{ formatDateTime(selectedMessage.created_at) }}</span>
          </li>
        </ul>

        <!-- Email Logs Table -->
        <div v-if="selectedMessage.send_type === 'email' || selectedMessage.send_type === 'both'">
          <h6 class="mb-2">
            <i class="material-icons icon-16pt mr-1" style="vertical-align: middle;">email</i>
            Email Delivery Log
          </h6>
          <b-skeleton-wrapper :loading="isLogsLoading">
            <template #loading>
              <b-skeleton-table :rows="4" :columns="4" />
            </template>
            <b-table
              :items="emailLogs"
              :fields="emailLogFields"
              responsive
              small
              bordered
              striped
              :busy="isLogsLoading"
              show-empty
              empty-text="No email log entries."
            >
              <template #cell(status)="{ item }">
                <span
                  :class="item.status === 'sent' ? 'badge badge-success' : 'badge badge-danger'"
                  style="text-transform: capitalize;"
                >
                  {{ item.status }}
                </span>
              </template>
              <template #cell(created_at)="{ item }">{{ formatDateTime(item.created_at) }}</template>
            </b-table>
            <pagination
              v-if="totalEmailLogs > emailLogs.length"
              v-model="emailLogsPage"
              :total-rows="totalEmailLogs"
              :per-page="logsPerPage"
              @change="onEmailLogsPageChange"
            />
          </b-skeleton-wrapper>
        </div>

        <!-- SMS Logs Table -->
        <div v-if="selectedMessage.send_type === 'sms' || selectedMessage.send_type === 'both'" class="mt-4">
          <h6 class="mb-2">
            <i class="material-icons icon-16pt mr-1" style="vertical-align: middle;">sms</i>
            SMS Delivery Log
          </h6>
          <b-skeleton-wrapper :loading="isLogsLoading">
            <template #loading>
              <b-skeleton-table :rows="4" :columns="5" />
            </template>
            <b-table
              :items="smsLogs"
              :fields="smsLogFields"
              responsive
              small
              bordered
              striped
              :busy="isLogsLoading"
              show-empty
              empty-text="No SMS log entries."
            >
              <template #cell(status)="{ item }">
                <span
                  :class="item.status === 'sent' ? 'badge badge-success' : 'badge badge-danger'"
                  style="text-transform: none;"
                >
                  {{ item.status === 'sent' ? '✓ Sent' : '✗ Failed' }}
                </span>
              </template>
              <template #cell(error_message)="{ item }">
                <span v-if="item.error_message" class="text-danger" style="font-size: 0.8rem;">{{
                  item.error_message
                }}</span>
                <span v-else class="text-muted">—</span>
              </template>
              <template #cell(created_at)="{ item }">{{ formatDateTime(item.created_at) }}</template>
            </b-table>
            <pagination
              v-if="totalSmsLogs > smsLogs.length"
              v-model="smsLogsPage"
              :total-rows="totalSmsLogs"
              :per-page="logsPerPage"
              @change="onSmsLogsPageChange"
            />
          </b-skeleton-wrapper>
        </div>
      </div>
    </b-modal>

    <!-- ══════════════════════════════════════
         THREAD PANEL MODAL
    ══════════════════════════════════════ -->
    <b-modal id="thread-panel-modal" title="Email Thread" size="lg" centered hide-footer @hidden="resetThreadModal">
      <b-skeleton-wrapper :loading="isThreadLoading">
        <template #loading>
          <b-skeleton width="60%" class="mb-3" />
          <b-skeleton width="40%" class="mb-4" />
          <b-skeleton-table :rows="3" :columns="1" />
        </template>

        <div v-if="selectedThread">
          <div v-if="selectedThread.subject" class="thread-header">
            <div class="thread-title">{{ selectedThread.subject }}</div>
          </div>

          <!-- Gmail-style collapsed messages -->
          <div v-if="selectedThread.replies && selectedThread.replies.length > 0">
            <!-- Original Campaign Message (collapsible) -->
            <div v-if="selectedThread.bulk_message" class="reply-item">
              <div class="reply-line d-flex" @click="toggleCampaignMessage" style="cursor: pointer;">
                <div
                  class="reply-avatar circle"
                  :style="selectedThread.avatar_url ? 'background: none; padding: 0;' : 'background: none;'"
                >
                  <img
                    v-if="selectedThread.avatar_url"
                    :src="selectedThread.avatar_url"
                    alt="org avatar"
                    style="width: 40px; height: 40px; border-radius: 50%; object-fit: cover;"
                  />
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center" style="gap: 6px; min-width: 0;">
                    <div class="reply-sender text-truncate">{{ selectedThread.organization_name || 'Transition' }}</div>
                    <div class="reply-meta">{{ selectedThread.subject }}</div>
                    <i
                      class="material-icons collapse-icon"
                      :style="showCampaignMessage ? 'transform: rotate(180deg);' : ''"
                      >expand_more</i
                    >
                  </div>
                  <div
                    v-if="selectedThread.organization_email || selectedThread.sender_email"
                    class="reply-email text-muted small"
                  >
                    {{ selectedThread.organization_email || selectedThread.sender_email }}
                  </div>
                  <div v-if="!showCampaignMessage" class="reply-preview mt-1 text-muted">
                    {{ getCampaignPreview() }}
                  </div>
                  <div v-else class="reply-body mt-1">
                    <div class="reply-content" v-html="getCampaignMessage()" />
                  </div>
                </div>
              </div>
            </div>

            <!-- First message (always expanded) -->
            <div class="reply-item">
              <div class="reply-line d-flex">
                <div class="reply-avatar circle">
                  {{
                    replyInitial({
                      sender_user_name: selectedThread.replies[0].sender_user_name,
                      sender_email: selectedThread.replies[0].sender_email,
                    })
                  }}
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center" style="gap: 6px; min-width: 0;">
                    <div class="reply-sender text-truncate">
                      {{ selectedThread.replies[0].sender_user_name || selectedThread.replies[0].sender_email }}
                    </div>
                    <div class="reply-meta">{{ formatDateTime(selectedThread.replies[0].created_at) }}</div>
                  </div>
                  <div v-if="selectedThread.replies[0].sender_email" class="reply-email text-muted small">
                    {{ selectedThread.replies[0].sender_email }}
                  </div>
                  <div class="reply-body mt-1">
                    <div v-if="selectedThread.replies[0].html_body">
                      <div class="reply-content" v-html="getReplyMainContent(selectedThread.replies[0].html_body)" />
                      <div v-if="hasQuotedContent(selectedThread.replies[0].html_body)" class="quoted-block">
                        <div class="quoted-toggle" @click.stop="toggleQuotedContent(selectedThread.replies[0].id)">
                          <i class="material-icons">{{
                            collapsedQuotes[selectedThread.replies[0].id] ? 'expand_more' : 'expand_less'
                          }}</i>
                          <span>{{ collapsedQuotes[selectedThread.replies[0].id] ? 'Show' : 'Hide' }} quoted text</span>
                        </div>
                        <div
                          v-show="!collapsedQuotes[selectedThread.replies[0].id]"
                          class="quoted-content-clean"
                          v-html="getQuotedContent(selectedThread.replies[0].html_body)"
                        />
                      </div>
                    </div>
                    <div v-else>
                      <div class="reply-content" style="white-space: pre-wrap;">
                        {{ getReplyMainContentPlain(selectedThread.replies[0].body) }}
                      </div>
                      <div v-if="hasQuotedContentPlain(selectedThread.replies[0].body)" class="quoted-block">
                        <div class="quoted-toggle" @click.stop="toggleQuotedContent(selectedThread.replies[0].id)">
                          <i class="material-icons">{{
                            collapsedQuotes[selectedThread.replies[0].id] ? 'expand_more' : 'expand_less'
                          }}</i>
                          <span>{{ collapsedQuotes[selectedThread.replies[0].id] ? 'Show' : 'Hide' }} quoted text</span>
                        </div>
                        <div v-show="!collapsedQuotes[selectedThread.replies[0].id]" class="quoted-content-clean">
                          {{ getQuotedContentPlain(selectedThread.replies[0].body) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Middle collapsed messages count (if more than 2 messages) -->
            <div
              v-if="selectedThread.replies.length > 2 && !showAllReplies"
              class="collapsed-messages-indicator"
              @click="showAllReplies = true"
            >
              <div class="reply-avatar circle collapsed-count-avatar">
                {{ selectedThread.replies.length - 2 }}
              </div>
              <div class="flex-grow-1 d-flex align-items-center" style="gap: 8px;">
                <div class="collapsed-line" />
              </div>
            </div>

            <!-- Middle messages (shown when expanded) -->
            <div v-if="showAllReplies && selectedThread.replies.length > 2">
              <div v-for="reply in selectedThread.replies.slice(1, -1)" :key="reply.id" class="reply-item">
                <div class="reply-line d-flex" @click="toggleReplyCollapse(reply.id)" style="cursor: pointer;">
                  <div class="reply-avatar circle">
                    {{ replyInitial({ sender_user_name: reply.sender_user_name, sender_email: reply.sender_email }) }}
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center" style="gap: 6px; min-width: 0;">
                      <div class="reply-sender text-truncate">{{ reply.sender_user_name || reply.sender_email }}</div>
                      <div class="reply-meta">{{ formatDateTime(reply.created_at) }}</div>
                      <i
                        class="material-icons collapse-icon"
                        :style="collapsedReplies[reply.id] ? '' : 'transform: rotate(180deg);'"
                        >expand_more</i
                      >
                    </div>
                    <div v-if="reply.sender_email" class="reply-email text-muted small">{{ reply.sender_email }}</div>
                    <!-- Preview (first line) when collapsed -->
                    <div v-if="collapsedReplies[reply.id]" class="reply-preview mt-1 text-muted">
                      {{ getReplyFirstLine(reply) }}
                    </div>
                    <!-- Full content when expanded -->
                    <div v-else class="reply-body mt-1">
                      <div v-if="reply.html_body">
                        <div class="reply-content" v-html="getReplyMainContent(reply.html_body)" />
                        <div v-if="hasQuotedContent(reply.html_body)" class="quoted-block">
                          <div class="quoted-toggle" @click.stop="toggleQuotedContent(reply.id)">
                            <i class="material-icons">{{
                              collapsedQuotes[reply.id] ? 'expand_more' : 'expand_less'
                            }}</i>
                            <span>{{ collapsedQuotes[reply.id] ? 'Show' : 'Hide' }} quoted text</span>
                          </div>
                          <div
                            v-show="!collapsedQuotes[reply.id]"
                            class="quoted-content-clean"
                            v-html="getQuotedContent(reply.html_body)"
                          />
                        </div>
                      </div>
                      <div v-else>
                        <div class="reply-content" style="white-space: pre-wrap;">
                          {{ getReplyMainContentPlain(reply.body) }}
                        </div>
                        <div v-if="hasQuotedContentPlain(reply.body)" class="quoted-block">
                          <div class="quoted-toggle" @click.stop="toggleQuotedContent(reply.id)">
                            <i class="material-icons">{{
                              collapsedQuotes[reply.id] ? 'expand_more' : 'expand_less'
                            }}</i>
                            <span>{{ collapsedQuotes[reply.id] ? 'Show' : 'Hide' }} quoted text</span>
                          </div>
                          <div v-show="!collapsedQuotes[reply.id]" class="quoted-content-clean">
                            {{ getQuotedContentPlain(reply.body) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Last message (always expanded if more than 1 message) -->
            <div v-if="selectedThread.replies.length > 1" class="reply-item">
              <div class="reply-line d-flex">
                <div class="reply-avatar circle">
                  {{
                    replyInitial({
                      sender_user_name: selectedThread.replies[selectedThread.replies.length - 1].sender_user_name,
                      sender_email: selectedThread.replies[selectedThread.replies.length - 1].sender_email,
                    })
                  }}
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center" style="gap: 6px; min-width: 0;">
                    <div class="reply-sender text-truncate">
                      {{
                        selectedThread.replies[selectedThread.replies.length - 1].sender_user_name ||
                          selectedThread.replies[selectedThread.replies.length - 1].sender_email
                      }}
                    </div>
                    <div class="reply-meta">
                      {{ formatDateTime(selectedThread.replies[selectedThread.replies.length - 1].created_at) }}
                    </div>
                  </div>
                  <div
                    v-if="selectedThread.replies[selectedThread.replies.length - 1].sender_email"
                    class="reply-email text-muted small"
                  >
                    {{ selectedThread.replies[selectedThread.replies.length - 1].sender_email }}
                  </div>
                  <div class="reply-body mt-1">
                    <div v-if="selectedThread.replies[selectedThread.replies.length - 1].html_body">
                      <div
                        class="reply-content"
                        v-html="
                          getReplyMainContent(selectedThread.replies[selectedThread.replies.length - 1].html_body)
                        "
                      />
                      <div
                        v-if="hasQuotedContent(selectedThread.replies[selectedThread.replies.length - 1].html_body)"
                        class="quoted-block"
                      >
                        <div
                          class="quoted-toggle"
                          @click.stop="
                            toggleQuotedContent(selectedThread.replies[selectedThread.replies.length - 1].id)
                          "
                        >
                          <i class="material-icons">{{
                            collapsedQuotes[selectedThread.replies[selectedThread.replies.length - 1].id]
                              ? 'expand_more'
                              : 'expand_less'
                          }}</i>
                          <span
                            >{{
                              collapsedQuotes[selectedThread.replies[selectedThread.replies.length - 1].id]
                                ? 'Show'
                                : 'Hide'
                            }}
                            quoted text</span
                          >
                        </div>
                        <div
                          v-show="!collapsedQuotes[selectedThread.replies[selectedThread.replies.length - 1].id]"
                          class="quoted-content-clean"
                          v-html="getQuotedContent(selectedThread.replies[selectedThread.replies.length - 1].html_body)"
                        />
                      </div>
                    </div>
                    <div v-else>
                      <div class="reply-content" style="white-space: pre-wrap;">
                        {{ getReplyMainContentPlain(selectedThread.replies[selectedThread.replies.length - 1].body) }}
                      </div>
                      <div
                        v-if="hasQuotedContentPlain(selectedThread.replies[selectedThread.replies.length - 1].body)"
                        class="quoted-block"
                      >
                        <div
                          class="quoted-toggle"
                          @click.stop="
                            toggleQuotedContent(selectedThread.replies[selectedThread.replies.length - 1].id)
                          "
                        >
                          <i class="material-icons">{{
                            collapsedQuotes[selectedThread.replies[selectedThread.replies.length - 1].id]
                              ? 'expand_more'
                              : 'expand_less'
                          }}</i>
                          <span
                            >{{
                              collapsedQuotes[selectedThread.replies[selectedThread.replies.length - 1].id]
                                ? 'Show'
                                : 'Hide'
                            }}
                            quoted text</span
                          >
                        </div>
                        <div
                          v-show="!collapsedQuotes[selectedThread.replies[selectedThread.replies.length - 1].id]"
                          class="quoted-content-clean"
                        >
                          {{ getQuotedContentPlain(selectedThread.replies[selectedThread.replies.length - 1].body) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="!selectedThread.replies || selectedThread.replies.length === 0"
            class="text-muted text-center py-4"
            style="font-size: 0.875rem;"
          >
            No replies in this thread.
          </div>

          <hr class="my-4" />
          <div>
            <label for="thread-reply-body" class="font-weight-bold mb-2">Reply as Organization</label>
            <b-form-textarea
              id="thread-reply-body"
              v-model.trim="threadReplyBody"
              rows="5"
              max-rows="12"
              placeholder="Write your reply (HTML or plain text)..."
            />
            <small class="text-muted d-block mt-2">This message will be sent to the selected email thread.</small>
          </div>
        </div>
      </b-skeleton-wrapper>

      <div class="d-flex justify-content-between align-items-center mt-3">
        <b-btn variant="light" @click="$bvModal.hide('thread-panel-modal')">Close</b-btn>
        <b-btn
          variant="primary"
          :disabled="isThreadReplySending || !threadReplyBody || !selectedReplyId"
          @click="submitThreadReply"
        >
          <b-spinner small v-if="isThreadReplySending" class="mr-2" />
          {{ isThreadReplySending ? 'Sending...' : 'Send Reply' }}
        </b-btn>
      </div>
    </b-modal>

    <bookmark-detail-modal :show-modal="showBookmarkDetails" @close="hideBookmarkDetails" abbr="bulk_messaging" />
  </div>
</template>

<script>
import { debounce, get, trim, truncate } from 'lodash';
import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { mapActions, mapGetters } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { USER_ROLES, USER_ROLE_TYPES, EMAIL_BOOKMARKS } from '@/common/constants';
import { formatDateTime } from '@/common/utils';
import Editor from '@tinymce/tinymce-vue';
import BookmarkDetailModal from '@/components/Ui/BookmarkDetailModal.vue';

export default {
  components: { Pagination, PageSeparator, Editor, BookmarkDetailModal },
  extends: Page,

  data() {
    return {
      title: 'Messages',
      USER_ROLES,
      USER_ROLE_TYPES,

      activeTab: 0,

      // ── Sent tab ──
      isLoading: false,
      isSending: false,
      areProgramsLoading: false,
      perPage: 14,
      currentPage: 1,
      totalMessages: 0,
      pageFromCount: 0,
      pageToCount: 0,
      messages: [],
      searchTerm: '',
      filters: {
        send_type: null,
        status: null,
        program: null,
      },
      allPrograms: [],
      selectedMessage: null,

      // ── Delivery log ──
      isLogsLoading: false,
      logsPerPage: 20,
      emailLogs: [],
      totalEmailLogs: 0,
      emailLogsPage: 1,
      smsLogs: [],
      totalSmsLogs: 0,
      smsLogsPage: 1,

      // ── Inbox tab ──
      isInboxLoading: false,
      replies: [],
      totalReplies: 0,
      inboxCurrentPage: 1,
      inboxPageFrom: 0,
      inboxPageTo: 0,
      inboxSearch: '',
      inboxUnreadOnly: false,
      inboxUnreadCount: 0,
      inboxCampaignFilter: null,
      inboxPollTimer: null,

      // ── Thread ──
      selectedThread: null,
      isThreadLoading: false,
      selectedReplyId: null,
      threadReplyBody: '',
      isThreadReplySending: false,
      collapsedReplies: {},
      collapsedQuotes: {},
      showAllReplies: false,
      showCampaignMessage: false,

      // ── Compose ──
      audienceType: 'all',
      dashboardStudents: [],
      isDashboardStudentsLoading: false,
      showAllStudents: false,
      manualEmailInput: '',
      manualEmailTags: [],

      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      editorKey: 0,
      EMAIL_BOOKMARKS,
      showBookmarkDetails: false,

      form: {
        subject: '',
        message: '',
        send_type: 'email',
        program: null,
        manual_emails: '',
        organization: null,
      },

      sendTypeOptions: [
        { value: null, text: 'All Types' },
        { value: 'email', text: 'Email' },
        { value: 'sms', text: 'SMS' },
        { value: 'both', text: 'Both' },
      ],

      statusOptions: [
        { value: null, text: 'All Statuses' },
        { value: 'pending', text: 'Pending' },
        { value: 'processing', text: 'Processing' },
        { value: 'sent', text: 'Sent' },
        { value: 'failed', text: 'Failed' },
      ],

      emailLogFields: [
        { key: 'recipient_user_name', label: 'NAME' },
        { key: 'recipient_email', label: 'EMAIL' },
        { key: 'status', label: 'STATUS' },
        { key: 'created_at', label: 'SENT AT' },
      ],

      smsLogFields: [
        { key: 'recipient_user_name', label: 'Name' },
        { key: 'recipient_phone', label: 'Phone' },
        { key: 'status', label: 'Status' },
        { key: 'error_message', label: 'Error' },
        { key: 'twilio_sid', label: 'Twilio SID' },
        { key: 'created_at', label: 'Sent At' },
      ],
    };
  },

  validations() {
    return {
      form: {
        subject: { required },
        message: { required },
        send_type: { required },
        organization: {
          required: requiredIf(() => this.isSuperAdmin),
        },
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    isSuperAdmin() {
      return get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN;
    },

    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Messages', active: true },
      ];
    },

    audienceLabel() {
      if (this.audienceType === 'all') return 'All Students';
      if (this.form.program) {
        const prog = this.allPrograms.find(p => p.id === this.form.program);
        return prog ? `${prog.title} Students` : 'Program Students';
      }
      return 'Select a program above';
    },

    visibleStudents() {
      if (this.showAllStudents) return this.dashboardStudents;
      return this.dashboardStudents.slice(0, 5);
    },

    manualStudentCandidates() {
      const byRecipient = new Map();
      (this.dashboardStudents || []).forEach(student => {
        const email = this.normalizeManualEmail(this.recipientEmail(student));
        const key = email || `student-${this.recipientKey(student)}`;
        if (!byRecipient.has(key)) {
          byRecipient.set(key, student);
        }
      });
      return Array.from(byRecipient.values());
    },

    manualVisibleStudents() {
      if (this.showAllStudents) return this.manualStudentCandidates;
      return this.manualStudentCandidates.slice(0, 5);
    },

    smsCharCount() {
      return (this.form.message || '').length;
    },

    manualRecipients() {
      return Array.from(
        new Set((this.manualEmailTags || []).map(email => this.normalizeManualEmail(email)).filter(Boolean)),
      );
    },

    manualEmailSuggestions() {
      const term = trim(this.manualEmailInput || '').toLowerCase();
      if (!term) return [];

      const selected = new Set(this.manualRecipients);

      const filtered = (this.dashboardStudents || []).filter(student => {
        const email = this.recipientEmail(student);
        if (!email || selected.has(email)) return false;

        const name = this.recipientName(student);
        return `${name} ${email}`.toLowerCase().includes(term);
      });

      return filtered.slice(0, 10);
    },
  },

  methods: {
    ...mapActions('bulkMessaging', [
      'getBulkMessages',
      'createBulkMessage',
      'getBulkMessageEmailLogs',
      'getBulkMessageSmsLogs',
      'getAvailablePrograms',
      'getEmailReplies',
      'getEmailThread',
      'replyToEmailThread',
      'markEmailReplyRead',
      'getBulkMessageDashboardStudents',
    ]),
    ...mapActions('program', ['getAllPrograms']),
    get,
    trim,
    truncate,
    formatDateTime,

    // ─── Helpers ───────────────────────────────────────────

    stripHtml(html) {
      if (!html) return '';
      const tokens = [];
      let result = html.replace(/{{(.*?)}}/g, (_, key) => {
        tokens.push(key);
        return `__BOOKMARK_${tokens.length - 1}__`;
      });
      result = result
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"');
      result = result.replace(/__BOOKMARK_(\d+)__/g, (_, i) => `{{${tokens[i]}}}`);
      return result.trim();
    },

    sendTypeLabel(type) {
      const map = { email: 'Email', sms: 'SMS', both: 'Email & SMS' };
      return map[type] || type;
    },

    sendTypeIcon(type) {
      const map = { email: 'email', sms: 'sms', both: 'forum' };
      return map[type] || 'send';
    },

    statusLabel(status) {
      const map = { pending: 'Pending', processing: 'Processing', sent: 'Sent', failed: 'Failed' };
      return map[status] || status;
    },

    statusBadgeClass(status) {
      const map = {
        pending: 'badge badge-warning',
        processing: 'badge badge-info',
        sent: 'badge badge-success',
        failed: 'badge badge-danger',
      };
      return map[status] || 'badge badge-secondary';
    },

    sentStatusLabel(msg) {
      if (msg.status !== 'sent') return null;
      const sent = msg.send_type === 'email' ? msg.email_count : msg.sms_count;
      const total = msg.total_recipients;
      if (!total) return null;
      if (sent === total) return null;
      if (sent === 0) return null;
      return `⚠ Partial (${sent}/${total})`;
    },

    sentStatusBadgeClass(msg) {
      if (msg.status !== 'sent') return 'd-none';
      const sent = msg.send_type === 'email' ? msg.email_count : msg.sms_count;
      const total = msg.total_recipients;
      if (!total || sent === total || sent === 0) return 'd-none';
      return 'badge badge-warning';
    },

    deliveryCountLabel(msg) {
      const sent = msg.send_type === 'sms' ? msg.sms_count : msg.email_count;
      return `${sent}/${msg.total_recipients} delivered`;
    },

    replyInitial(thread) {
      const name = thread.sender_user_name || thread.sender_email || '?';
      return name.charAt(0).toUpperCase();
    },

    recipientName(student) {
      return student.name || student.student_name || student.full_name || 'Unknown Student';
    },

    recipientEmail(student) {
      return student.email || student.student_email || student.user_email || null;
    },

    recipientInitial(student) {
      const value = this.recipientName(student) || this.recipientEmail(student) || '?';
      return value.charAt(0).toUpperCase();
    },

    recipientKey(student) {
      return student.student_id || student.user_id || student.id || this.recipientEmail(student);
    },

    normalizeManualEmail(email) {
      const raw = trim(email || '');
      if (!raw) return null;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(raw)) return null;

      return raw.toLowerCase();
    },

    parseManualEmails(value) {
      const raw = typeof value === 'string' ? value : '';
      const tokens = raw
        .split(/[\n,;]+/)
        .map(item => this.normalizeManualEmail(item))
        .filter(Boolean);
      return Array.from(new Set(tokens));
    },

    syncManualEmailsField() {
      this.form.manual_emails = this.manualRecipients.join('\n');
    },

    addManualRecipient(email) {
      const normalized = this.normalizeManualEmail(email);
      if (!normalized) return false;
      if (this.manualRecipients.includes(normalized)) return true;

      this.manualEmailTags = [...this.manualRecipients, normalized];
      this.syncManualEmailsField();
      return true;
    },

    addManualRecipientFromStudent(student) {
      const email = this.recipientEmail(student);
      if (!email) {
        this.makeToast({ variant: 'warning', msg: 'Selected student does not have a valid email.' });
        return;
      }
      this.addManualRecipient(email);
      this.manualEmailInput = '';
    },

    isManualRecipientSelected(student) {
      const email = this.normalizeManualEmail(this.recipientEmail(student));
      return !!email && this.manualRecipients.includes(email);
    },

    toggleManualRecipientFromStudent(student) {
      const email = this.normalizeManualEmail(this.recipientEmail(student));
      if (!email) {
        this.makeToast({ variant: 'warning', msg: 'Selected student does not have a valid email.' });
        return;
      }

      if (this.manualRecipients.includes(email)) {
        this.removeManualRecipient(email);
      } else {
        this.addManualRecipient(email);
      }
    },

    addManualRecipientsFromInput() {
      const raw = trim(this.manualEmailInput || '');
      if (!raw) return;

      const suggestionMatch = (this.dashboardStudents || []).find(student => {
        const name = this.recipientName(student).toLowerCase();
        const email = (this.recipientEmail(student) || '').toLowerCase();
        const needle = raw.toLowerCase();
        return name === needle || email === needle;
      });
      if (suggestionMatch) {
        this.addManualRecipientFromStudent(suggestionMatch);
        return;
      }

      const parsed = this.parseManualEmails(raw);
      if (!parsed.length) {
        this.makeToast({ variant: 'warning', msg: 'Please enter a valid email address.' });
        return;
      }

      parsed.forEach(email => this.addManualRecipient(email));
      this.manualEmailInput = '';
    },

    removeManualRecipient(email) {
      this.manualEmailTags = this.manualRecipients.filter(item => item !== email);
      this.syncManualEmailsField();
    },

    manualRecipientInitial(email) {
      const value = trim(email || '');
      if (!value) return '?';
      return value.charAt(0).toUpperCase();
    },

    // ─── Programs ───────────────────────────────────────────

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms({ ordering: 'id' });
        this.allPrograms = res.data.results || res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },

    // ─── Sent list ──────────────────────────────────────────

    onFilterApplied() {
      this.fetchMessages();
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    onManualEmailInput() {
      if (this.audienceType !== 'manual') return;
      this.debouncedManualEmailSearch(this);
    },

    debouncedSearch: debounce(vm => {
      vm.fetchMessages();
    }, 500),

    debouncedManualEmailSearch: debounce(vm => {
      vm.fetchDashboardStudents(vm.manualEmailInput);
    }, 350),

    async fetchMessages(pageNum = 1) {
      this.isLoading = true;
      if (document.getElementById('page-container')) {
        document.getElementById('page-container').scrollIntoView();
      }
      try {
        const params = {
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ordering: '-created_at',
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.filters.status && { status: this.filters.status }),
          ...(this.filters.program && { program: this.filters.program }),
          send_type: 'email',
        };
        const response = await this.getBulkMessages(params);
        this.messages = response.data.results || response.data;
        this.currentPage = pageNum;
        this.totalMessages =
          response.data.count || (response.data.results ? response.data.results.length : response.data.length);
        this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
        this.pageToCount = this.pageFromCount + get(this.messages, 'length', 0) - 1;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchMessages(pageNum);
    },

    // ─── Delivery Log Drawer ────────────────────────────────

    async openDeliveryLogDrawer(msg) {
      this.selectedMessage = { ...msg };
      this.emailLogs = [];
      this.smsLogs = [];
      this.totalEmailLogs = 0;
      this.totalSmsLogs = 0;
      this.emailLogsPage = 1;
      this.smsLogsPage = 1;
      this.$bvModal.show('delivery-log-modal');
      await this.fetchDeliveryLogs();
    },

    onDeliveryLogHidden() {},

    async fetchDeliveryLogs() {
      this.isLogsLoading = true;
      try {
        const id = this.selectedMessage.id;
        if (this.selectedMessage.send_type === 'email' || this.selectedMessage.send_type === 'both') {
          const res = await this.getBulkMessageEmailLogs({
            id,
            params: { limit: this.logsPerPage, offset: (this.emailLogsPage - 1) * this.logsPerPage },
          });
          this.emailLogs = get(res, 'results', res || []);
          this.totalEmailLogs = get(res, 'count', this.emailLogs.length);
        }
        if (this.selectedMessage.send_type === 'sms' || this.selectedMessage.send_type === 'both') {
          const res = await this.getBulkMessageSmsLogs({
            id,
            params: { limit: this.logsPerPage, offset: (this.smsLogsPage - 1) * this.logsPerPage },
          });
          this.smsLogs = get(res, 'results', res || []);
          this.totalSmsLogs = get(res, 'count', this.smsLogs.length);
        }
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLogsLoading = false;
    },

    async onEmailLogsPageChange(page) {
      this.emailLogsPage = page;
      await this.fetchDeliveryLogs();
    },

    async onSmsLogsPageChange(page) {
      this.smsLogsPage = page;
      await this.fetchDeliveryLogs();
    },

    // ─── Inbox ──────────────────────────────────────────────

    onTabChange(tab) {
      if (tab === 1) {
        // Clear campaign filter only if not coming from notification
        if (!this.$route.query.bulkMessageId) {
          this.inboxCampaignFilter = null;
        }
        this.fetchReplies();
        this.startInboxPoll();
      } else {
        this.stopInboxPoll();
        // Clear query param when leaving inbox tab
        if (this.$route.query.bulkMessageId) {
          this.$router.replace({ name: 'org-bulk-messaging' });
        }
      }
    },

    onInboxSearch() {
      this.debouncedInboxSearch(this);
    },

    debouncedInboxSearch: debounce(vm => {
      vm.fetchReplies();
    }, 500),

    async fetchReplies(pageNum = 1) {
      this.isInboxLoading = true;
      try {
        const params = {
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ordering: '-created_at',
          ...(this.inboxSearch && { search: this.inboxSearch }),
          ...(this.inboxUnreadOnly && { is_read: false }),
          ...(this.inboxCampaignFilter && { bulk_message: this.inboxCampaignFilter }),
        };
        const response = await this.getEmailReplies(params);
        this.replies = get(response, 'data.results', response.data || []);
        this.inboxCurrentPage = pageNum;
        this.totalReplies = get(response, 'data.count', this.replies.length);
        this.inboxPageFrom = (pageNum - 1) * this.perPage + 1;
        this.inboxPageTo = this.inboxPageFrom + this.replies.length - 1;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isInboxLoading = false;
    },

    onInboxPageChange(page) {
      this.fetchReplies(page);
    },

    openInboxForCampaign(campaignId) {
      // Use query params to maintain filter state
      this.$router
        .push({
          name: 'org-bulk-messaging',
          query: { bulkMessageId: campaignId },
        })
        .catch(() => {
          // If already on page, just update the query
          this.$router.replace({
            name: 'org-bulk-messaging',
            query: { bulkMessageId: campaignId },
          });
        });
    },

    clearCampaignFilter() {
      this.inboxCampaignFilter = null;
      // Clear query param from URL
      if (this.$route.query.bulkMessageId) {
        this.$router.replace({ name: 'org-bulk-messaging' });
      }
      this.fetchReplies();
    },

    startInboxPoll() {
      this.stopInboxPoll();
      const poll = async () => {
        await this.fetchUnreadCount();
        this.inboxPollTimer = setTimeout(poll, 30000);
      };
      this.inboxPollTimer = setTimeout(poll, 30000);
    },

    stopInboxPoll() {
      clearTimeout(this.inboxPollTimer);
      this.inboxPollTimer = null;
    },

    // ─── Thread ─────────────────────────────────────────────

    async openThread(thread) {
      this.selectedThread = { ...thread };
      this.isThreadLoading = true;
      this.selectedReplyId =
        thread.replies && thread.replies.length > 0 ? thread.replies[thread.replies.length - 1].id : null;
      this.threadReplyBody = '';
      this.showAllReplies = false;

      this.$bvModal.show('thread-panel-modal');

      // Fetch the complete thread with all messages
      try {
        const response = await this.getEmailThread({
          senderEmail: thread.sender_email,
          bulkMessageId: thread.bulk_message,
        });

        const threadData = response.data && response.data.length > 0 ? response.data[0] : thread;
        this.selectedThread = { ...threadData };
        this.selectedReplyId =
          threadData.replies && threadData.replies.length > 0
            ? threadData.replies[threadData.replies.length - 1].id
            : null;

        // Collapse all middle replies by default
        this.collapsedReplies = {};
        if (threadData.replies && threadData.replies.length > 2) {
          threadData.replies.slice(1, -1).forEach(reply => {
            this.$set(this.collapsedReplies, reply.id, true);
          });
        }

        // Collapse quoted text blocks by default
        this.collapsedQuotes = {};
        if (threadData.replies && threadData.replies.length > 0) {
          threadData.replies.forEach(reply => {
            this.$set(this.collapsedQuotes, reply.id, true);
          });
        }
      } catch (e) {
        // Fallback to using the thread data we already have
        this.collapsedReplies = {};
        if (thread.replies && thread.replies.length > 2) {
          thread.replies.slice(1, -1).forEach(reply => {
            this.$set(this.collapsedReplies, reply.id, true);
          });
        }

        // Collapse quoted text blocks by default
        this.collapsedQuotes = {};
        if (thread.replies && thread.replies.length > 0) {
          thread.replies.forEach(reply => {
            this.$set(this.collapsedQuotes, reply.id, true);
          });
        }
      }

      this.isThreadLoading = false;

      // Scroll to reply composer after modal opens
      this.$nextTick(() => {
        const replyTextarea = document.getElementById('thread-reply-body');
        if (replyTextarea) {
          replyTextarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });

      // Mark all unread replies as read
      if (!thread.is_read && thread.replies && thread.replies.length > 0) {
        try {
          for (const reply of thread.replies) {
            if (!reply.is_read) {
              await this.markEmailReplyRead(reply.id);
            }
          }
          const idx = this.replies.findIndex(
            t => t.sender_email === thread.sender_email && t.bulk_message === thread.bulk_message,
          );
          if (idx !== -1) {
            this.$set(this.replies[idx], 'is_read', true);
          }
          if (this.inboxUnreadCount > 0) this.inboxUnreadCount--;
        } catch (e) {
          // non-critical
        }
      }

      this.isThreadLoading = false;
    },

    resetThreadModal() {
      this.selectedThread = null;
      this.selectedReplyId = null;
      this.threadReplyBody = '';
      this.isThreadReplySending = false;
      this.isThreadLoading = false;
      this.collapsedReplies = {};
      this.collapsedQuotes = {};
      this.showAllReplies = false;
      this.showCampaignMessage = false;
    },

    toggleCampaignMessage() {
      this.showCampaignMessage = !this.showCampaignMessage;
    },

    getCampaignPreview() {
      if (!this.selectedThread || !this.selectedThread.replies || this.selectedThread.replies.length === 0) return '';
      const firstReply = this.selectedThread.replies[0];
      if (firstReply.html_body) {
        const quotedContent = this.getQuotedContent(firstReply.html_body);
        if (!quotedContent && firstReply.in_reply_to === null) return '';
        const mainContent = quotedContent || this.getReplyMainContent(firstReply.html_body);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = mainContent;
        const text = (tempDiv.textContent || tempDiv.innerText || '').trim();
        return text.length > 100 ? `${text.substring(0, 100).trim()}...` : text;
      } else if (firstReply.body) {
        const quotedContent = this.getQuotedContentPlain(firstReply.body);
        if (!quotedContent && firstReply.in_reply_to === null) return '';
        const mainContent = quotedContent || this.getReplyMainContentPlain(firstReply.body);
        const text = (mainContent || '').trim();
        return text.length > 100 ? `${text.substring(0, 100).trim()}...` : text;
      }
      return this.selectedThread.subject || '';
    },

    getCampaignMessage() {
      if (!this.selectedThread || !this.selectedThread.replies || this.selectedThread.replies.length === 0) return '';
      const firstReply = this.selectedThread.replies[0];
      if (firstReply.html_body) {
        return this.getQuotedContent(firstReply.html_body);
      } else if (firstReply.body) {
        return `<div style="white-space: pre-wrap;">${this.getQuotedContentPlain(firstReply.body)}</div>`;
      }
      return '';
    },

    getReplyFirstLine(reply) {
      let text = '';
      if (reply.html_body) {
        text = this.stripHtml(this.getReplyMainContent(reply.html_body));
      } else {
        text = this.getReplyMainContentPlain(reply.body);
      }
      const firstLine = text.split('\n')[0];
      return firstLine.length > 80 ? firstLine.substring(0, 80) + '...' : firstLine;
    },

    toggleReplyCollapse(replyId) {
      this.$set(this.collapsedReplies, replyId, !this.collapsedReplies[replyId]);
    },

    toggleQuotedContent(replyId) {
      this.$set(this.collapsedQuotes, replyId, !this.collapsedQuotes[replyId]);
    },

    hasQuotedContent(htmlBody) {
      if (!htmlBody) return false;
      return htmlBody.includes('gmail_quote') || htmlBody.includes('<blockquote');
    },

    hasQuotedContentPlain(plainBody) {
      if (!plainBody) return false;
      return plainBody.includes('\n>') || (plainBody.includes('On ') && plainBody.includes('wrote:'));
    },

    getReplyMainContent(htmlBody) {
      if (!htmlBody) return '';
      const quoteStart = htmlBody.indexOf('<div class="gmail_quote');
      if (quoteStart === -1) {
        const blockquoteStart = htmlBody.indexOf('<blockquote');
        if (blockquoteStart === -1) return htmlBody;
        return htmlBody.substring(0, blockquoteStart);
      }
      return htmlBody.substring(0, quoteStart);
    },

    getQuotedContent(htmlBody) {
      if (!htmlBody) return '';
      let quotedHtml = '';
      const quoteStart = htmlBody.indexOf('<div class="gmail_quote');
      if (quoteStart !== -1) {
        quotedHtml = htmlBody.substring(quoteStart);
      } else {
        const blockquoteStart = htmlBody.indexOf('<blockquote');
        if (blockquoteStart !== -1) {
          quotedHtml = htmlBody.substring(blockquoteStart);
        }
      }

      // Extract clean text from the quoted content
      return this.extractCleanQuotedText(quotedHtml);
    },

    extractCleanQuotedText(htmlContent) {
      if (!htmlContent) return '';

      // Create a temporary div to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;

      // Remove tracking pixels and images
      const images = tempDiv.querySelectorAll('img');
      images.forEach(img => {
        if (img.width === 1 && img.height === 1) {
          img.remove();
        }
      });

      // Process nested blockquotes to create Gmail-style threaded display
      const processBlockquote = (element, level = 0) => {
        let result = '';

        // Find the gmail_attr (header line)
        const attrDiv = element.querySelector('.gmail_attr');
        if (attrDiv) {
          const headerText = attrDiv.textContent.trim();
          result += `<div style="color: #5f6368; font-size: 0.85rem; margin: 4px 0;">${headerText}</div>`;
        }

        // Find the blockquote
        const blockquote = element.querySelector('blockquote');
        if (blockquote) {
          // Get the direct text content (not from nested blockquotes)
          const clone = blockquote.cloneNode(true);
          const nestedBlockquotes = clone.querySelectorAll('blockquote');
          nestedBlockquotes.forEach(nq => nq.remove());

          let messageText = clone.textContent || clone.innerText || '';
          messageText = messageText.replace(/\s+/g, ' ').trim();

          // Remove email footers
          messageText = messageText.replace(/©\s*\d{4}.*$/i, '');
          messageText = messageText.replace(/Regards,?\s*[\w\s]+Team\s*$/i, '');

          if (messageText) {
            result += `<div style="border-left: 2px solid #ccc; padding-left: 12px; margin: 4px 0; color: #202124; font-size: 0.85rem;">${messageText}</div>`;
          }

          // Process nested blockquotes recursively
          const nestedQuote = blockquote.querySelector('blockquote');
          if (nestedQuote) {
            const nestedContainer = document.createElement('div');
            nestedContainer.appendChild(nestedQuote.cloneNode(true));
            result += `<div style="margin-left: 12px;">${processBlockquote(nestedContainer, level + 1)}</div>`;
          }
        }

        return result;
      };

      return processBlockquote(tempDiv) || htmlContent;
    },

    getReplyMainContentPlain(plainBody) {
      if (!plainBody) return '';
      const lines = plainBody.split('\n');
      const mainLines = [];
      for (const line of lines) {
        if (line.trim().startsWith('>') || (line.includes('On ') && line.includes('wrote:'))) {
          break;
        }
        mainLines.push(line);
      }
      return mainLines.join('\n').trim();
    },

    getQuotedContentPlain(plainBody) {
      if (!plainBody) return '';
      const lines = plainBody.split('\n');
      const result = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Check if it's a header line
        if (line.includes('On ') && line.includes('wrote:')) {
          result.push(line.trim());
          continue;
        }

        // Count quote level
        const quoteMatch = line.match(/^(>+)\s*(.*)/);
        if (quoteMatch) {
          const level = quoteMatch[1].length;
          let content = quoteMatch[2].trim();

          // Remove email signatures
          content = content.replace(/Regards,?[\s\S]*$/i, '');
          content = content.replace(/©\s*\d{4}.*$/i, '');

          if (content) {
            const indent = '  '.repeat(level);
            result.push(indent + content);
          }
        } else if (line.trim()) {
          result.push(line.trim());
        }
      }

      return result.join('\n');
    },

    async submitThreadReply() {
      if (!this.selectedReplyId || !this.threadReplyBody) return;

      this.isThreadReplySending = true;
      try {
        await this.replyToEmailThread({
          id: this.selectedReplyId,
          body: this.threadReplyBody,
        });

        this.makeToast({ variant: 'success', msg: 'Reply sent successfully.' });
        this.threadReplyBody = '';

        // Refresh the thread to show the new organization reply
        if (this.selectedThread) {
          const response = await this.getEmailThread({
            senderEmail: this.selectedThread.sender_email,
            bulkMessageId: this.selectedThread.bulk_message,
          });

          if (response.data && response.data.length > 0) {
            const threadData = response.data[0];
            this.selectedThread = { ...threadData };
            this.selectedReplyId =
              threadData.replies && threadData.replies.length > 0
                ? threadData.replies[threadData.replies.length - 1].id
                : null;

            // Keep all messages expanded after sending a reply
            this.showAllReplies = true;
          }
        }

        await this.fetchReplies(this.inboxCurrentPage);
      } catch (err) {
        const errMsg =
          get(err, 'response.data.message') || get(err, 'response.data.detail') || this.$t('generalMsgs.genErrorMsg');
        this.makeToast({ variant: 'danger', msg: errMsg });
      }
      this.isThreadReplySending = false;
    },

    // ─── Compose ────────────────────────────────────────────

    openComposeModal() {
      this.$bvModal.show('compose-bulk-message-modal');
      this.$nextTick(() => {
        this.editorKey += 1;
      });
      this.fetchDashboardStudents();
    },

    async fetchDashboardStudents(searchValue = '') {
      this.isDashboardStudentsLoading = true;
      try {
        const params = {};
        if (this.audienceType === 'manual') {
          const term = trim(searchValue || this.manualEmailInput || '');
          if (term) {
            params.search = term;
          }
        } else if (this.audienceType === 'program' && this.form.program) {
          params.program = this.form.program;
        }
        const res = await this.getBulkMessageDashboardStudents(params);
        const payload = get(res, 'data', []);
        this.dashboardStudents = Array.isArray(payload) ? payload : get(payload, 'results', []);
      } catch (e) {
        this.dashboardStudents = [];
      }
      this.isDashboardStudentsLoading = false;
    },

    onAudienceTypeChange() {
      this.showAllStudents = false;
      if (this.audienceType !== 'manual') {
        this.manualEmailInput = '';
        this.manualEmailTags = [];
        this.form.manual_emails = '';
      }
      this.fetchDashboardStudents();
    },

    onProgramChange() {
      this.showAllStudents = false;
      this.fetchDashboardStudents();
    },

    toggleShowAllStudents() {
      this.showAllStudents = !this.showAllStudents;
    },

    openBookmarkDetails() {
      this.showBookmarkDetails = true;
    },

    hideBookmarkDetails() {
      this.showBookmarkDetails = false;
    },

    resetForm() {
      this.form = {
        subject: '',
        message: '',
        send_type: 'email',
        program: null,
        manual_emails: '',
        organization: null,
      };
      this.audienceType = 'all';
      this.dashboardStudents = [];
      this.showAllStudents = false;
      this.manualEmailInput = '';
      this.manualEmailTags = [];
      this.$v.$reset();
    },

    async onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.makeToast({ variant: 'danger', msg: 'Please fill all required fields.' });
        return;
      }

      if (this.audienceType === 'manual' && !this.manualRecipients.length) {
        this.makeToast({ variant: 'danger', msg: 'Please enter at least one valid email address.' });
        return;
      }

      this.isSending = true;
      try {
        const data = {
          subject: this.form.subject,
          message: this.form.message,
          send_type: this.form.send_type,
          ...(this.form.program && { program: this.form.program }),
          ...(this.audienceType === 'manual' &&
            this.manualRecipients.length && { manual_emails: this.manualRecipients }),
        };

        if (this.isSuperAdmin && this.form.organization) {
          data.organization = this.form.organization;
        } else if (!this.isSuperAdmin) {
          const orgId = get(this.getLoggedInUser, 'linked_entity.id');
          if (orgId) data.organization = orgId;
        }

        const res = await this.createBulkMessage(data);
        const created = get(res, 'data.data', get(res, 'data', {}));

        this.makeToast({ variant: 'success', msg: 'Bulk message queued for sending!' });
        this.$bvModal.hide('compose-bulk-message-modal');

        // Optimistically prepend to list then open detail with polling
        if (created.id) {
          this.messages = [created, ...this.messages];
          this.totalMessages++;
          this.openDeliveryLogDrawer(created);
        } else {
          this.fetchMessages();
        }
      } catch (err) {
        const errMsg =
          get(err, 'response.data.message') || get(err, 'response.data.detail') || this.$t('generalMsgs.genErrorMsg');
        this.makeToast({ variant: 'danger', msg: errMsg });
      }
      this.isSending = false;
    },
  },

  async mounted() {
    await this.fetchPrograms();
    this.fetchMessages();

    // Check if navigated from email_reply_received notification
    if (this.$route.query.bulkMessageId) {
      this.inboxCampaignFilter = parseInt(this.$route.query.bulkMessageId);
      this.activeTab = 1;
      this.fetchReplies();
      this.startInboxPoll();
    } else {
      // Periodic unread count refresh while on page
      this.startInboxPoll();
    }
  },

  watch: {
    '$route.query.bulkMessageId'(newVal) {
      if (newVal) {
        this.inboxCampaignFilter = parseInt(newVal);
        this.activeTab = 1;
        this.fetchReplies();
        this.startInboxPoll();
      }
    },
  },

  beforeDestroy() {
    this.stopInboxPoll();
  },
};
</script>

<style scoped>
.reply-item {
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}

.reply-line {
  gap: 12px;
}

.manual-recipient-chip {
  font-size: 0.72rem;
  padding: 5px 9px;
  font-weight: 400;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  color: #495057;
}

.manual-recipient-chip--selected {
  border-color: #adb5bd;
  background: #f8f9fa;
}

.manual-recipient-check {
  font-size: 12px;
  color: #6c757d;
}

.reply-avatar.circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e8eaed;
  font-weight: 600;
  font-size: 0.85rem;
  color: #5f6368;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.reply-sender {
  font-size: 0.95rem;
  color: #202124;
}

.reply-meta {
  font-size: 0.78rem;
  color: #5f6368;
}

.reply-body {
  padding-right: 16px;
}

.reply-content {
  font-size: 0.92rem;
  color: #202124;
  line-height: 1.55;
}

.quoted-block {
  margin-top: 6px;
}

.quoted-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #5f6368;
  font-size: 0.82rem;
  cursor: pointer;
  user-select: none;
}

.quoted-toggle i {
  font-size: 16px;
}

.quoted-content {
  border-left: 2px solid #e0e0e0;
  padding-left: 10px;
  margin-top: 4px;
  font-size: 0.85rem;
  color: #5f6368;
  line-height: 1.45;
}

.quoted-content-clean {
  border-left: 2px solid #e0e0e0;
  padding: 8px 12px;
  margin-top: 8px;
  font-size: 0.85rem;
  color: #5f6368;
  line-height: 1.5;
  background-color: #f8f9fa;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.collapse-icon {
  color: #5f6368;
  font-size: 18px;
  transition: transform 0.15s ease;
}

.collapsed-messages-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  margin: 2px 0;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

.collapsed-messages-indicator:hover {
  background-color: #f8f9fa;
}

.collapsed-line {
  flex: 1;
  height: 1px;
  background: #e0e0e0;
}

.collapsed-count-avatar {
  width: 32px;
  height: 32px;
  background: #f1f3f4;
  color: #5f6368;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid #636060;
  flex-shrink: 0;
}

.collapsed-chevron {
  color: #5f6368;
  font-size: 18px;
  line-height: 1;
  flex: 0 0 auto;
}

.reply-preview {
  font-size: 0.85rem;
  color: #5f6368;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.thread-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.thread-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #202124;
}

.thread-chips {
  display: flex;
  gap: 6px;
  align-items: center;
}

.chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
}

.chip-grey {
  background: #f1f3f4;
  color: #5f6368;
  border-color: #e0e0e0;
}

.chip-gold {
  background: #fdd835;
  color: #8a6d1f;
  border-color: #fbc02d;
}
</style>
